/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect, useContext } from 'react'
import Modal from '../../../../components/Modal'
import { useServiceContext } from '../../../../root'
import { RcmTableDataContext } from '../../../../components/rcm-table/RcmTableDataContext'
import EditForm from './EditForm'
import { toast } from '../../../../services/toast'
import { changeRequestTypesApiService } from '../../../../services/api/api.changerequesttypes'
import { getAll } from '../../../../services/api/metadata'
import { transformFromServer, transformToServer } from './DataService'
import ValidationSchema from './EditFormValidation'

interface ModalEditProps {
  template?: any
  onClose: () => void
  onSuccess?: () => void
}

const ModalEdit: React.FC<ModalEditProps> = ({ template, onClose, onSuccess }) => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const tableContext = useContext(RcmTableDataContext)

  const [isEdit, setIsEdit] = useState(!!template)
  const [isOpen, setIsOpen] = useState(true)
  const [loading, setLoading] = useState<boolean>(false)
  const [loadedTemplate, setLoadedTemplate] = useState<any>(null)
  const [initialValues, setInitialValues] = useState<any>(null)
  const [dataLoaded, setDataLoaded] = useState<boolean>(false) // Track if data is fully loaded
  const validationSchema = ValidationSchema()

  useEffect(() => {
    if (isEdit && template?.id) {
      // Set empty initial values immediately so modal can open
      const emptyTemplate = {}
      const transformed = transformFromServer(emptyTemplate, [])
      const defaults = validationSchema.cast(transformed, {
        stripUnknown: false,
        context: transformed
      })
      setInitialValues(defaults)
      setDataLoaded(false) // Mark data as not loaded yet
      // Then load actual data
      loadData()
    } else {
      // For add mode, set initial values immediately
      const emptyTemplate = {}
      const transformed = transformFromServer(emptyTemplate, [])
      const defaults = validationSchema.cast(transformed, {
        stripUnknown: false,
        context: transformed
      })

      setInitialValues(defaults)
      setDataLoaded(true) // Add mode doesn't need to load data
    }
  }, [template?.id, isEdit])

  const loadData = async () => {
    try {
      setLoading(true)

      // Load both metadata templates and template data in parallel (like legacy)
      const [metadataResponse, templateData] = await Promise.all([
        getAll({ sortBy: 'NAME', sortDesc: false }),
        changeRequestTypesApiService.getById(template!.id, false)
      ])

      // Transform server data to form format with metadata templates
      const transformed = transformFromServer(templateData, metadataResponse.elements)
      // Apply validation schema defaults
      const defaults = validationSchema.cast(transformed, {
        stripUnknown: false,
        context: transformed
      })

      setLoadedTemplate(templateData)
      setInitialValues(defaults)
      setDataLoaded(true) // Mark data as fully loaded
      setLoading(false)
    } catch (error) {
      console.error('Error loading template:', error)
      setLoading(false)
      setDataLoaded(false)
      toast.error(translate('ERROR_LOADING_TEMPLATE'))
      doClose()
    }
  }

  const doSave = async (values: any) => {
    try {
      // Transform form data to server format
      const serverData = transformToServer(values)

      setLoading(true)

      if (isEdit) {
        await changeRequestTypesApiService.update(serverData.id, serverData)
      } else {
        await changeRequestTypesApiService.create(serverData)
      }

      toast.success(translate(isEdit ? 'WF_TEMPLATES_UPDATE_SUCCESS_MSG' : 'WF_TEMPLATES_CREATE_SUCCESS_MSG'))

      // Refresh the table - go to page 1 to see new/updated item
      if (tableContext?.refreshTable) {
        tableContext.refreshTable()
      } else {
        console.warn('Table context not available for refresh')
      }

      if (onSuccess) {
        onSuccess()
      }

      doClose()
    } catch (error: any) {
      // ApiError has body property, not data
      let errorMsg = translate('ERROR')
      if (error?.body) {
        // Try different error message formats from API
        errorMsg = error.body.message || error.body.error || error.body.errorMessage || JSON.stringify(error.body)
      } else if (error?.message) {
        errorMsg = error.message
      }
      if (isEdit) {
        toast.error(translate('WF_TEMPLATES_UPDATE_FAIL_MSG', { error: errorMsg }))
      } else {
        toast.error(translate('CHANGE_REQUEST_TYPES_CREATE_FAIL_MSG', { error: errorMsg }))
      }
    } finally {
      setLoading(false)
    }
  }

  const doClose = () => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  if (!isOpen) {
    return null
  }

  // Don't render form until initialValues are ready
  if (!initialValues) {
    return null
  }

  return (
    <Modal
      show={isOpen}
      handleClose={doClose}
      title={translate(isEdit ? 'WORKFLOW_TEMPLATES_EDIT_TITLE' : 'WORKFLOW_TEMPLATES_ADD_TITLE')}
      size="lg"
      dataTestId="edit-workflow-template-modal"
    >
      <EditForm
        onSubmit={doSave}
        doClose={doClose}
        isEdit={isEdit}
        initialValues={initialValues}
        loading={loading}
        dataLoaded={dataLoaded}
      />
    </Modal>
  )
}

export default ModalEdit
